import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/afsia-solar-awards.webp";
import HeroImgMobile       from "../../assets/images/blogs/afsia-solar-awards-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";

/* ─────────────────────────────────────────────
   WinnersOfAFSIASolarAwardsAnnounced.jsx
   Prefix      : aw-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   DIFF vs other pages:
   - Hero title     : CENTERED (1 line)
   - aw-subhead--bold : bold opening para (16px 700)
   - aw-category    : bold category label (16px 700, margin-top 44px)
   - aw-category-desc: regular para under each category
   - aw-subhead     : standard subhead "An exceptional and global platform"
   - No inline images, no tweets, no bullets
   - Newsletter     : prop-based isMobile, Subscribe button
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .aw-wrap *, .aw-wrap *::before, .aw-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .aw-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .aw-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .aw-content-col { width: 100%; }

  /* ── SCROLL FADE-UP ── */
  .aw-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .aw-fade-up.aw-in { opacity: 1; transform: translateY(0); }
  .aw-delay-1 { transition-delay: 0.08s; }
  .aw-delay-2 { transition-delay: 0.18s; }
  .aw-delay-3 { transition-delay: 0.28s; }
  .aw-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO — centered title
  ══════════════════════════════════════ */
  .aw-hero { width: 100%; background: #1A1A1A; }
  .aw-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .aw-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: center;
  }
  .aw-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .aw-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .aw-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 100px;
  }

  /* bold opening para — image 2 */
  .aw-subhead--bold {
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 160%;
    color: #ffffff;
    margin-bottom: 14px;
  }

  /* regular paragraph */
  .aw-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  /* category label — bold, margin-top spacing */
  .aw-category {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: #ffffff;
    line-height: 160%;
    margin-top: 44px;
    margin-bottom: 10px;
  }

  /* category description — regular para, tighter margin */
  .aw-category-desc {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 0;
  }

  /* standard subhead — "An exceptional and global platform" */
  .aw-subhead {
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #ffffff;
    line-height: 160%;
    margin-bottom: 14px;
    margin-top: 44px;
  }

  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .aw-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .aw-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
   
    object-position: center;
    z-index: 0;
  }
  .aw-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .aw-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .aw-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .aw-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .aw-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .aw-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .aw-newsletter__input::placeholder { color: #FFFFFF; }
  .aw-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .aw-newsletter__btn:hover { background: #5e2e90; }
  .aw-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .aw-newsletter__btn:hover .aw-newsletter__btn-arrow { transform: translateX(4px); }
  .aw-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .aw-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .aw-inner { padding-left: 32px; padding-right: 32px; }
    .aw-hero__img-wrap img { max-height: 350px; }
    .aw-hero__title-bar { padding: 24px 0px 20px; }
    .aw-hero__title { font-size: 24px; }
    .aw-body-section { padding: 40px 0 48px; }
    .aw-para { margin-bottom: 18px; font-size: 15px; }
    .aw-subhead--bold { font-size: 15px; margin-bottom: 18px; }
    .aw-category { font-size: 15px; margin-top: 28px; margin-bottom: 8px; }
    .aw-category-desc { font-size: 15px; }
    .aw-subhead { font-size: 18px; margin-top: 28px; margin-bottom: 10px; }

    .aw-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .aw-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .aw-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .aw-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .aw-newsletter__heading { font-size: 24px; }
    .aw-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .aw-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .aw-newsletter__input { border-radius: 5px; height: 44px; }
    .aw-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .aw-inner { padding-left: 16px; padding-right: 16px; }
    .aw-hero__title-bar { padding: 30px 0px; }
    .aw-body-section { padding: 30px 0 30px; }
    .aw-hero__img-wrap img { max-height: 366px; width: 100%; }
    .aw-hero__title { font-size: 26px; line-height: 120%; }
    .aw-para { font-size: 14px; margin-bottom: 14px; }
    .aw-subhead--bold { font-size: 14px; margin-bottom: 14px; }
    .aw-category { font-size: 14px; margin-top: 30px; margin-bottom: 8px; }
    .aw-category-desc { font-size: 14px; }
    .aw-subhead { margin-top: 30px; margin-bottom: 14px; font-size: 16px; }

    .aw-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .aw-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .aw-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .aw-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .aw-newsletter__heading { font-size: 30px; line-height: 120%; }
    .aw-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .aw-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .aw-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .aw-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".aw-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("aw-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="aw-newsletter">
      {!isMobile && (
        <>
          <img className="aw-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="aw-newsletter__overlay" />
        </>
      )}
      <div className="aw-newsletter__inner">
        {isMobile && (
          <img className="aw-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="aw-newsletter__content">
          <h2 className="aw-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="aw-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="aw-newsletter__form">
            <input
              className="aw-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="aw-newsletter__btn">
              Subscribe
              <span className="aw-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function WinnersOfAFSIASolarAwardsAnnounced() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="aw-wrap">

        {/* HERO — centered title */}
        <section className="aw-hero">
          <div className="aw-hero__title-bar">
            <div className="aw-inner">
              <h1 className="aw-hero__title">Winners of AFSIA Solar Awards announced</h1>
            </div>
          </div>
          <div className="aw-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="AFSIA Solar Awards"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="aw-body-section">
          <div className="aw-inner">
            <div className="aw-content-col">

              {/* bold opening para — image 2 */}
              <p className="aw-subhead--bold aw-fade-up aw-delay-1">The inaugural Africa Solar Industry Association (AFSIA) Solar Awards, organized in partnership with the Africa Energy Forum (aef), culminated in a grand show streamed online, during which the winners of 15 categories were unveiled and celebrated.</p>

              <p className="aw-para aw-fade-up aw-delay-1">John van Zuylen, the founder of AFSIA, commented: "AFSIA Solar Awards was delighted to be hosted by this much-respected program and to unveil the winners during a digital ceremony."</p>

              <p className="aw-para aw-fade-up aw-delay-1">The winners are:</p>

              {/* category entries — images 2 & 3 */}
              <p className="aw-category aw-fade-up aw-delay-1">Utility-Scale Project of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">Sterling &amp; Wilson Solar for the multi-projects 322MW contribution to Egypt's 1.6GW Benban Solar Complex</p>

              <p className="aw-category aw-fade-up aw-delay-1">Commercial &amp; Industrial Solar Project of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">CrossBoundary Energy for the Jabi Lake Mall project in Abuja, Nigeria</p>

              <p className="aw-category aw-fade-up aw-delay-1">Mini-Grid Project of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">Africa GreenTec for their productive-use based Mini-Grid projects in Mali, Niger and Senegal, which are often subject to high-security risks.</p>

              <p className="aw-category aw-fade-up aw-delay-1">Solar Home System Company of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">d.light for achieving the target of reaching 100 million people with solar energy.</p>

              <p className="aw-category aw-fade-up aw-delay-1">Residential Project of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">Munyax Eco for exceptional achievement in the field of Solar Water heaters having installed more than 1,000 units across Rwanda saving 8,000 tons of CO2 per year.</p>

              <p className="aw-category aw-fade-up aw-delay-1">African Solar company of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">ANKA Madagascar for securing 5MW worth of mini-grids across Madagascar and for the company's innovative AgriGrid model.</p>

              <p className="aw-category aw-fade-up aw-delay-1">African Solar SME of the year: (two winners)</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">
                Pawame for reaching 80,000 Kenyans with Solar Home System solutions while achieving cash-flow break-even and profitability.<br />
                And<br />
                Solar Box Gabon for developing the "Solar Cube" which doubles the production of traditional solar panels.
              </p>

              <p className="aw-category aw-fade-up aw-delay-1">Legal Advisor of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">Eversheds Sutherland for their contribution to the Open Solar Contracts with IRENA, the International Renewable Energy Agency, and the Terrawatt Initiative, providing open-source standardized contracts to governments across the globe.</p>

              <p className="aw-category aw-fade-up aw-delay-1">Technical Advisor of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">Suntrace for technical advisory services to the Fekola Gold mine hybrid project in Mali, a first of its kind off-grid hybrid project composed of 36MW of solar, 15.4MWh of storage, and saving 13 million liters of heavy fuel oil annually.</p>

              <p className="aw-category aw-fade-up aw-delay-1">DFI of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">African Development Bank (AfDB) for establishing SEFA, the Sustainable Energy Fund for Africa, which has contributed among others first-of-its-kind inventory finance facility for solar irrigation products in East Africa.</p>

              <p className="aw-category aw-fade-up aw-delay-1">Woman in Solar of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">Olaedo Osoka CEO of Daystar Power Ghana, for leading the expansion of the company from Nigeria to Ghana, Togo and Senegal and realizing C&amp;I projects of 8MW in this 2-years period… only at the age of 27!</p>

              <p className="aw-category aw-fade-up aw-delay-1">Solar Innovation for the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">Phaesun for their work on the RevivED Water Innovation project. This project is a solar-powered desalination and purification system for brackish water based on electrodialysis technology providing up to 2000l of clean drinking per day.</p>

              <p className="aw-category aw-fade-up aw-delay-1">Solar Picture of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">Alexandre Skander Allegue — Pawame for a beautiful picture highlighting the impact of lighting in the most remote areas.</p>

              <p className="aw-category aw-fade-up aw-delay-1">Solar Video of the year:</p>
              <p className="aw-category-desc aw-fade-up aw-delay-1">Joanna Gentili — African Minigrids for a super inspiring and motivating video about electrifying a village in Malawi.</p>

              {/* standard subhead + paras — image 4 */}
              <h2 className="aw-subhead aw-fade-up aw-delay-1">An exceptional and global platform</h2>

              <p className="aw-para aw-fade-up aw-delay-1">According to the organizers, more than 130 entries were submitted across the different categories from all over the continent over the past few months.</p>

              <p className="aw-para aw-fade-up aw-delay-1">The jury, composed of leading experts in solar energy in Africa, carefully evaluated each entry to identify the most deserving companies and individuals to be awarded the grand prize during this online ceremony.</p>

              <p className="aw-para aw-fade-up aw-delay-1">Among the 15 categories in the competition this year, the most disputed titles included "C&amp;I Project of the Year" and "Mini-Grid Project of the Year", which is reflective of the increased activity of both of these segments of the African solar industry.</p>

              <p className="aw-para aw-fade-up aw-delay-1">"Woman in Solar of the Year" was another strong category among participants. This highlights the very positive trend of the growing involvement of women in the solar industry across the continent.</p>

              <p className="aw-para aw-fade-up aw-delay-1">The awards form part of aef's program for the six-week-long Digital Energy Festival for Africa, which it is hosting along with African Utility Week &amp; POWERGEN Africa, the Oil &amp; Gas Council's Africa Assembly and ESI Africa. The largest-ever energy event for the African continent, the festival continues until 26 November.</p>

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}